/**
 * ErrorBoundary — 全局错误边界与降级渲染
 * 任一组件抛错时局部降级为赛博警告卡片，绝不让单组件故障白屏整站。
 * 用法：<ErrorBoundary label="HUD">…</ErrorBoundary>
 */
import { Component, type ReactNode } from "react";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** 故障节点名（调试用） */
  label?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown): void {
    // 保留诊断线索，不向用户暴露堆栈
    console.error(`[ErrorBoundary:${this.props.label ?? "anon"}]`, error, info);
  }

  private handleRestart = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="neo-card w-full border border-[#f97316]/50 p-6 text-center"
          style={{ boxShadow: "0 0 12px rgba(249,115,22,0.25)" }}
        >
          <div className="mx-auto mb-3 h-2 w-2 animate-pulse rounded-full bg-[#f97316]" />
          <div className="font-pixel text-[10px] leading-relaxed tracking-[0.2em] text-[#f97316]">
            [ CRITICAL SYSTEM FAULT // 节点离线，正在重启... ]
          </div>
          <div className="mt-2 font-mono text-[10px] text-[#94a3b8]">
            NODE: {this.props.label ?? "UNKNOWN"} — 局部降级，其余系统继续在线
          </div>
          <button
            type="button"
            className="px-btn mt-4"
            onClick={this.handleRestart}
          >
            [ 重新连接 / REBOOT ]
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
